import { OfferCoupon } from '../../../../../services/fidelity/fidelity';

/**
 * Encode le code du bon et l'identifiant KingFisher en un token Base64URL
 * afin qu'aucune donnée ne soit lisible à l'œil nu dans l'URL.
 *
 * Note : il s'agit d'une obfuscation visuelle, pas d'un chiffrement sécurisé.
 * La clé de décodage (Base64URL) est publique ; l'objectif est uniquement d'éviter
 * l'exposition en clair du `kfId` et du code dans la barre d'adresse.
 */
export const encodeVoucherToken = (code: string, kfId: string): string =>
  btoa(JSON.stringify({ code, kfId }))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

/**
 * Décode un token produit par `encodeVoucherToken`.
 * Retourne `null` si le token est invalide ou mal formé.
 */
export const decodeVoucherToken = (token: string): { code: string; kfId: string } | null => {
  try {
    const base64Token = token
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(token.length / 4) * 4, '=');
    const parsed = JSON.parse(atob(base64Token));
    if (typeof parsed?.code === 'string' && typeof parsed?.kfId === 'string') {
      return parsed as { code: string; kfId: string };
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Construit l'URL publique vers la page bon de réduction du site client.
 * Le code du bon et le `kfId` sont encodés ensemble en Base64 dans le
 * segment de route `:token` - aucun paramètre n'apparaît en clair dans l'URL.
 *
 * @param voucher        Le bon de réduction à partager.
 * @param kfId           Identifiant KingFisher du client.
 * @param customerAppUrl URL racine du site client (ex. "https://logiciels-conception.castorama.fr").
 */
export const buildVoucherUrl = (voucher: OfferCoupon, kfId: string, customerAppUrl: string): string => {
  const token = encodeVoucherToken(voucher.code, kfId);
  return `${customerAppUrl}/voucher/${token}`;
};
